import { actions, selectors } from 'data'
import { ExtraKYCContext, Node } from 'data/types'
import messages from 'lang'
import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import Container from 'scenes/Modal/Container'
import styled from 'styled-components'
import { color } from 'utils'

import FinishedStep from './Steps/FinishedStep'
import KYCStep from './Steps/KYCStep'

export const Wrapper = styled.div`
  background: ${color('dark800')};
  color: ${color('white')};

  div:first-child span {
    color: ${color('white')};
  }
  div div label {
    color: ${color('dark200')};
  }
`

type Props = {
  context: ExtraKYCContext
  onDone: () => void
}
export const ExtraKyc = ({ context, onDone }: Props) => {
  const extraKYCQuestions = useSelector(selectors.auth.extraKYCQuestions.get)
  const dispatch = useDispatch()
  const [currentStep, setCurrentStep] = useState(0)
  const [previousStep, setPreviousStep] = useState<number | null>(null)
  // inner state, we collect user input in this object
  const [questions, setQuestions] = useState<Array<Node>>([])
  // ids of steps done
  const [doneQuestions, setDoneQuestions] = useState<Array<string>>([])
  const intel = useIntl()

  useEffect(() => {
    if (extraKYCQuestions) {
      setQuestions(extraKYCQuestions.nodes)
    }
  }, [extraKYCQuestions])

  useEffect(() => {
    dispatch(actions.analytics.trackEvent({ key: 'Account Info Screen Viewed', properties: {} }))
  }, [])

  useEffect(() => {
    // Fetch questions if we haven't already
    if (!extraKYCQuestions) {
      dispatch(actions.auth.extraKYCQuestions.fetch(context))
    }
  }, [])

  const saveQuestion = (question: Node) => {
    // Done this way to preserve array sorting
    const editedQuestions = [...questions]
    const editedIndex = questions.findIndex((q) => q.id === question.id)
    editedQuestions[editedIndex] = question
    setQuestions(editedQuestions)
    return editedQuestions
  }

  const onEditStep = (questionToEdit) => {
    setPreviousStep(currentStep)
    setCurrentStep(questions.indexOf(questionToEdit))
    setDoneQuestions(doneQuestions.filter((id) => id !== questionToEdit.id))
  }

  const onQuestionDone = (question: Node) => {
    const totalSteps = questions.length - 1

    const newQuestions = saveQuestion(question)

    if (currentStep < totalSteps) {
      setDoneQuestions([...doneQuestions, question.id])
      if (previousStep) {
        setCurrentStep(previousStep)
        setPreviousStep(null)
      } else {
        setCurrentStep(currentStep + 1)
      }
      return
    }
    // Done with last step
    dispatch(actions.auth.extraKYCQuestions.submit(newQuestions))
    onDone()
    dispatch(actions.analytics.trackEvent({ key: 'Account Info Screen Submitted', properties: {} }))
  }

  if (!questions.length) return null
  const openQuestion = questions[currentStep]

  return (
    <Wrapper>
      <Container theme='dark' title={intel.formatMessage(messages.use_of_account_information)}>
        {questions
          .filter((q) => doneQuestions.includes(q.id))
          .map((question) => (
            <FinishedStep
              key={question.id}
              title={question.text}
              onEdit={() => onEditStep(question)}
            />
          ))}
        {openQuestion && (
          <KYCStep question={openQuestion} key={openQuestion.id} onDone={onQuestionDone} />
        )}
      </Container>
    </Wrapper>
  )
}
